// @ts-nocheck

import React, { useEffect, useCallback, useLayoutEffect, useRef, useState, cloneElement } from 'react';
import type { ElementRef, Node, Element } from 'react';
import { createPortal } from 'react-dom';
import ResizeDetector from 'react-resize-detector';
import styled, { css } from 'styled-components';

import { Target } from '../../client';
import { NAVBAR_BASE_HEIGHT } from '../../constants/ui/navbar';
import { TABBAR_BASE_HEIGHT } from '../../constants/ui/tabbar';
import { useViewportContext } from '../../contexts/ViewportContext';
import { getAppDOMElement } from '../../DOMElements';
import theme from '../../styled-components/dark';

type Props = {
  children: Element<unknown>;
  placeholderStyle?: $Shape<CSSStyleDeclaration>;
  disabled?: boolean;
  placeholderBackgroundColor?: string;
};

type withStickyPlaceholderProps = {
  hasStickyContent: boolean;
  disabled?: boolean;
  placeholderStyle?: $Shape<CSSStyleDeclaration>;
};

const Placeholder = styled.div`
  padding-top: 56.25%;
  overflow: hidden;
  width: 100%;
  background-color: ${({ $backgroundColor }) => $backgroundColor || 'transparent'};
  position: relative;
`;

const StickyWrapper = styled.div`
  left: ${Target.__MOBILE__ ? 0 : 'auto'};

  ${({ $isFixed, $offsetTop, $computedWidth }) => css`
    position: ${$isFixed ? 'fixed' : 'absolute'};
    width: ${$computedWidth}px;

    ${Target.__MOBILE__
      ? css`
          z-index: ${$isFixed ? 12 : 'auto'};
          margin: ${$isFixed ? 0 : `${NAVBAR_BASE_HEIGHT}em 0 ${TABBAR_BASE_HEIGHT}em 0`};
          margin: ${$isFixed
            ? 0
            : `calc(${NAVBAR_BASE_HEIGHT}em + constant(safe-area-inset-top)) constant(safe-area-inset-right)
            0 constant(safe-area-inset-left)`};
          margin: ${$isFixed
            ? 0
            : `calc(${NAVBAR_BASE_HEIGHT}em + env(safe-area-inset-top)) env(safe-area-inset-right)
            0 env(safe-area-inset-left)`};

          top: ${$isFixed
            ? `calc(${$offsetTop}px + constant(safe-area-inset-top))`
            : `calc(${$offsetTop}px - constant(safe-area-inset-top))`};
          top: ${$isFixed
            ? `calc(${$offsetTop}px + env(safe-area-inset-top))`
            : `calc(${$offsetTop}px - env(safe-area-inset-top))`};
        `
      : css`
          z-index: ${$isFixed ? theme.ZIndices.middleColumn : 'auto'};
          top: 0;
        `}
  `}
`;

const StickyContent = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`;

const StickyWithPlaceholder = ({
  placeholderBackgroundColor,
  placeholderStyle,
  disabled = false,
  children,
}: Props): Node => {
  const { fontSize, orientation, navbarHeight } = useViewportContext();

  const navbarHeightWithoutSafeArea = NAVBAR_BASE_HEIGHT * fontSize;

  const [isSticky, setSticky] = useState(false);
  const [distanceFromTop, setDistanceFromTop] = useState(0);
  const [placeholderWidth, setPlaceholderWidth] = useState(0);
  const [placeholderSize, setPlaceholderSize] = useState({
    width: 0,
    height: 0,
  });
  const placeholderRef = useRef<ElementRef<'div'> | null>(null);

  const finalIsSticky = !disabled && isSticky;
  const scrollElement = Target.__MOBILE__ ? getAppDOMElement() : window;

  const computePosition = useCallback(() => {
    if (placeholderRef.current) {
      const { top, width } = placeholderRef.current.getBoundingClientRect();
      setPlaceholderWidth(width);
      if (scrollElement) {
        setDistanceFromTop(Math.floor(top + (scrollElement.scrollY || scrollElement.scrollTop || 0)));
      }
    }
  }, [scrollElement]);

  useLayoutEffect(() => {
    computePosition();

    // if the dom change above the sticky element, we need to recompute the distanceFromTop value.
    // this ugly setInterval is here to deal with this issue.
    const interval = setInterval(computePosition, 500);
    return () => clearInterval(interval);
  }, [finalIsSticky, computePosition, orientation]);

  useEffect(() => {
    const handleScroll = () => {
      if (placeholderRef.current) {
        const distanceToTop = placeholderRef.current.getBoundingClientRect().top;
        setSticky(Target.__MOBILE__ ? orientation === 2 && distanceToTop <= navbarHeight : distanceToTop <= 0);
      }
    };
    if (scrollElement && !disabled) {
      scrollElement.addEventListener('scroll', handleScroll);
      return () => {
        scrollElement.removeEventListener('scroll', handleScroll);
      };
    }
  }, [disabled, scrollElement, orientation, navbarHeight]);

  useEffect(() => setSticky(false), [orientation]);

  const handleResize = useCallback((width, height) => {
    setPlaceholderSize({ width, height });
  }, []);

  // NB: we don't use `navbarHeight` from ViewportContext but `navbarHeightWithoutSafeArea` to not include the safe area values
  // The reason is that at the embbeded app loading, getting safe area values with JS returns `0` and it creates some side effects
  const offsetTop = finalIsSticky ? navbarHeightWithoutSafeArea : distanceFromTop - navbarHeightWithoutSafeArea;

  const renderChildren = useCallback(
    () => (
      <StickyWrapper $computedWidth={placeholderWidth} $isFixed={finalIsSticky} $offsetTop={offsetTop}>
        <StickyContent>
          <div>
            {cloneElement(children, { offsetTop, isSticky: finalIsSticky, placeholderRef })}
            <ResizeDetector handleHeight handleWidth onResize={handleResize} />
          </div>
        </StickyContent>
      </StickyWrapper>
    ),
    [children, finalIsSticky, handleResize, placeholderWidth, offsetTop],
  );

  return (
    <Placeholder
      style={{ ...placeholderStyle, paddingTop: placeholderSize.height }}
      ref={placeholderRef}
      $backgroundColor={placeholderBackgroundColor}
    >
      {Target.__MOBILE__ && scrollElement && getAppDOMElement()
        ? createPortal(renderChildren(), getAppDOMElement())
        : renderChildren()}
    </Placeholder>
  );
};

/**
 * HOC which returns an enhanced component with a sticky placeholder or not
 *
 * @param {Node} WrappedComponent the component which will be enhanced
 */
export const withStickyPlaceholder = (WrappedComponent: unknown) => {
  return function AnonymousComponent(props: withStickyPlaceholderProps) {
    const { hasStickyContent, disabled = false, placeholderStyle, ...rest } = props;

    return (
      <>
        {hasStickyContent ? (
          <StickyWithPlaceholder disabled={disabled} placeholderStyle={placeholderStyle}>
            <WrappedComponent {...rest} />
          </StickyWithPlaceholder>
        ) : (
          <WrappedComponent {...rest} />
        )}
      </>
    );
  };
};

export default StickyWithPlaceholder;
